import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { trustedBrands } from '../data/mock';
import { caseStudies } from '../data/caseStudies';

const BRAND_LOGOS = {
  nike: 'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/6708jfdd_Nike-Logo.png',
  comisario: 'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/bhqjatih_client-comisario-removebg-preview.png',
};

function MiniLogo({ k }) {
  const cls = 'flex items-center justify-center text-black w-full h-full px-6';

  if (BRAND_LOGOS[k]) {
    return (
      <div className={cls + ' py-3'}>
        <img
          src={BRAND_LOGOS[k]}
          alt={k}
          className="h-10 md:h-12 w-auto max-w-[70%] object-contain"
          draggable={false}
        />
      </div>
    );
  }

  switch (k) {
    case 'comisario':
      return (
        <div className={cls + ' flex-col'}>
          <p className="font-serif italic text-[10px] tracking-[0.3em]">TEQUILA</p>
          <p className="font-serif text-2xl tracking-tight">Comisario</p>
          <p className="font-serif italic text-[7px] tracking-[0.3em]">100% PURO DE AGAVE</p>
        </div>
      );
    case 'jones':
      return (
        <div className={cls + ' flex-col'}>
          <p className="font-display text-xl tracking-[0.15em]">JONES</p>
          <p className="font-display text-[9px] tracking-[0.4em]">SODA CO.</p>
        </div>
      );
    case 'delta':
      return (
        <div className={cls}>
          <svg viewBox="0 0 160 40" className="w-full max-w-[180px]">
            <polygon points="5,32 22,8 39,32" fill="#000" />
            <polygon points="14,32 22,20 30,32" fill="#F4EFE4" />
            <text x="50" y="28" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="22" letterSpacing="3" fill="#000">DELTA</text>
          </svg>
        </div>
      );
    case 'nike':
      return (
        <div className={cls}>
          <svg viewBox="0 0 180 70" className="w-full max-w-[180px]">
            <text x="50%" y="55%" textAnchor="middle" fontFamily="Impact, sans-serif" fontSize="42" fontStyle="italic" fontWeight="900" fill="#000">NIKE</text>
            <path d="M25 50 C60 28 115 24 160 32 L152 42 C115 36 70 42 35 53 Z" fill="#000" />
          </svg>
        </div>
      );
    case 'mercedes':
      return (
        <div className={cls}>
          <svg viewBox="0 0 200 40" className="w-full max-w-[200px]">
            <circle cx="22" cy="20" r="13" fill="none" stroke="#000" strokeWidth="1.5" />
            <path d="M22 8 L22 20 M22 20 L12 27 M22 20 L32 27" stroke="#000" strokeWidth="1.5" />
            <text x="45" y="27" fontFamily="Georgia, serif" fontSize="17" fill="#000">Mercedes-Benz</text>
          </svg>
        </div>
      );
    default:
      return <div className={cls}>{k}</div>;
  }
}

export default function TrustedBy() {
  // Build dynamic destinations from the actual case-study data — no hardcoded URL map.
  const items = trustedBrands
    .map((slug) => {
      const cs = caseStudies[slug];
      if (!cs) return null;
      return {
        slug,
        to: `/work/${slug}`,
        label: cs.client,
        badge: cs.comingSoon
          ? `COMING SOON · ${cs.comingSoonYear || 'SOON'}`
          : 'CASE STUDY',
        ribbon: cs.comingSoon ? 'PREVIEW THE LAUNCH' : 'READ THE CASE STUDY',
      };
    })
    .filter(Boolean);

  const loop = [...items, ...items];

  return (
    <section className="py-14 md:py-20 border-t border-white/5" data-testid="trusted-by">
      <div className="px-5 md:px-10 lg:px-14 mb-8 flex items-center gap-3">
        <span className="w-8 h-px bg-[#FF3D2E]" />
        <p className="text-[10px] md:text-[11px] tracking-[0.3em] text-white/55">
          TRUSTED BY CATEGORY-DEFINING BRANDS
        </p>
        <span className="hidden md:inline text-[10px] tracking-[0.3em] text-white/30">·</span>
        <p className="hidden md:inline text-[10px] tracking-[0.3em] text-white/45">
          HOVER TO READ THE WORK
        </p>
      </div>

      <div className="relative overflow-hidden group/marquee">
        <div className="flex animate-marquee-slow group-hover/marquee:[animation-play-state:paused] gap-5 w-max">
          {loop.map((b, i) => (
            <Link
              key={i}
              to={b.to}
              aria-label={`Read the ${b.label} case study`}
              data-testid={`trusted-tile-${b.slug}-${i}`}
              className="group relative bg-[#F4EFE4] hover:bg-[#EFE9DA] w-[240px] md:w-[320px] h-[120px] md:h-[140px] flex items-center justify-center shrink-0 transition-colors overflow-hidden"
            >
              {/* Corner badge */}
              <span className="absolute top-3 left-3 text-[9px] tracking-[0.3em] text-black/60 group-hover:text-black transition-colors">
                {b.badge}
              </span>

              {/* Arrow */}
              <ArrowUpRight className="absolute top-3 right-3 w-4 h-4 text-black/40 group-hover:text-black group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />

              {/* Logo */}
              <div className="transition-transform duration-500 ease-out group-hover:scale-[1.06] group-hover:-translate-y-1">
                <MiniLogo k={b.slug} />
              </div>

              {/* Slide-up ribbon on hover */}
              <span className="absolute bottom-0 left-0 right-0 bg-black text-white text-[10px] tracking-[0.3em] py-2 text-center translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-out">
                {b.ribbon} →
              </span>
            </Link>
          ))}
        </div>

        {/* Edge fades so the marquee feels continuous */}
        <span className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-black to-transparent" />
        <span className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-black to-transparent" />
      </div>
    </section>
  );
}
